import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Star, Sparkles, Copy, Target, ChevronLeft, ChevronRight, Loader2, Search,
  ChevronDown, ChefHat, Clock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  portalGet, portalPost, portalDelete, portalPut, hu, todayISO, shiftDate, prettyDate, apiError,
} from '../../lib/portalApi';

const SLOTS = [
  { v: 'reggeli', t: 'Reggeli' },
  { v: 'tizorai', t: 'Tízórai' },
  { v: 'ebed', t: 'Ebéd' },
  { v: 'uzsonna', t: 'Uzsonna' },
  { v: 'vacsora', t: 'Vacsora' },
  { v: 'egyeb', t: 'Egyéb' },
];

const MacroBar = ({ label, value, target, color }) => {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  const over = target > 0 && value > target;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-semibold text-gray-700">{label}</span>
        <span className={over ? 'font-bold text-red-600' : 'text-gray-600'}>
          {hu(value)}{target > 0 ? ` / ${hu(target)}` : ''} g
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${over ? 'bg-red-500' : color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const FoodDiaryTab = () => {
  const [date, setDate] = useState(todayISO());
  const [log, setLog] = useState(null);
  const [foods, setFoods] = useState([]);
  const [q, setQ] = useState('');
  const [slot, setSlot] = useState('reggeli');
  const [grams, setGrams] = useState(100);
  const [picked, setPicked] = useState(null);
  const [portions, setPortions] = useState(1);
  const [openRecipe, setOpenRecipe] = useState(null);
  const [aiText, setAiText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showOwn, setShowOwn] = useState(false);
  const [view, setView] = useState('foods');
  const [recipes, setRecipes] = useState([]);
  const [diet, setDiet] = useState([]);
  const [suggest, setSuggest] = useState(null);
  const [own, setOwn] = useState({ name: '', kcal_100: '', protein_100: '', carbs_100: '', fat_100: '' });

  const loadLog = (d = date) => portalGet('/food-log', { date: d }).then(setLog).catch(() => {});
  const loadFoods = (needle = '') => portalGet('/foods', { q: needle, only: 'food' }).then(r => setFoods(r.foods)).catch(() => {});
  const loadRecipes = (needle = '', diets = diet) =>
    portalGet('/recipes', { q: needle, diet: diets.join(',') }).then(r => setRecipes(r.recipes)).catch(() => {});

  useEffect(() => { loadLog(date); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => { loadFoods(''); }, []);
  useEffect(() => {
    const id = setTimeout(() => (view === 'recipes' ? loadRecipes(q) : loadFoods(q)), 250);
    return () => clearTimeout(id);
    /* eslint-disable-next-line */
  }, [q, view, diet]);

  const totals = log?.totals || { kcal: 0, protein: 0, carbs: 0, fat: 0 };
  const targets = log?.targets || { kcal: 0, protein: 0, carbs: 0, fat: 0, label: '' };
  const remaining = Math.round((targets.kcal || 0) - (totals.kcal || 0));

  const isRecipe = picked?.source === 'recipe';
  const listRows = view === 'recipes' ? recipes : foods;

  const computed = useMemo(() => {
    if (!picked) return null;
    if (picked.source === 'recipe') {
      const m = Number(portions) || 0;
      return {
        kcal: Math.round((picked.kcal || 0) * m),
        protein: +((picked.protein || 0) * m).toFixed(1),
        carbs: +((picked.carbs || 0) * m).toFixed(1),
        fat: +((picked.fat || 0) * m).toFixed(1),
      };
    }
    const g = Number(grams) || 0;
    const f = g / 100;
    return {
      kcal: Math.round(picked.kcal_100 * f),
      protein: +(picked.protein_100 * f).toFixed(1),
      carbs: +(picked.carbs_100 * f).toFixed(1),
      fat: +(picked.fat_100 * f).toFixed(1),
    };
  }, [picked, grams, portions]);

  const addPicked = async () => {
    if (!picked || !computed) return;
    setBusy(true);
    try {
      await portalPost('/food-log', {
        date,
        name: picked.name,
        grams: isRecipe ? 0 : Number(grams) || 0,
        portions: isRecipe ? Number(portions) || 1 : null,
        ...computed,
        meal_slot: slot, source: picked.source,
      });
      toast.success(`${picked.name} naplózva`);
      setPicked(null);
      await loadLog();
    } catch (err) { toast.error(apiError(err, 'Nem sikerült naplózni.')); } finally { setBusy(false); }
  };

  const aiEstimate = async () => {
    if (aiText.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await portalPost('/foods/estimate', { text: aiText });
      const e = r.estimate;
      await portalPost('/food-log', {
        date, name: e.name, grams: e.grams, kcal: e.kcal, protein: e.protein,
        carbs: e.carbs, fat: e.fat, meal_slot: slot, source: 'ai',
      });
      toast.success(`${e.name} — kb. ${hu(e.kcal)} kcal naplózva`);
      setAiText('');
      await loadLog();
    } catch (err) {
      toast.error(apiError(err, 'A becslés nem sikerült.'));
    } finally { setBusy(false); }
  };

  const removeEntry = async (id) => {
    try {
      await portalDelete(`/food-log/${id}`);
      await loadLog();
    } catch (err) { toast.error(apiError(err, 'A törlés nem sikerült.')); }
  };

  const copyYesterday = async () => {
    setBusy(true);
    try {
      const r = await portalPost('/food-log/copy-day', { from_date: shiftDate(date, -1), to_date: date });
      toast.success(`${r.copied} tétel átmásolva a tegnapi napról`);
      await loadLog();
    } catch (err) {
      toast.error(apiError(err, 'Nincs mit másolni.'));
    } finally { setBusy(false); }
  };

  const toggleFav = async (name) => {
    try {
      await portalPost('/foods/favorite', { name });
      if (view === 'recipes') await loadRecipes(q); else await loadFoods(q);
    } catch (err) { toast.error(apiError(err, 'Nem sikerült menteni.')); }
  };

  const loadSuggest = async () => {
    setBusy(true);
    try {
      const r = await portalGet('/food-log/suggest', { date, meal_slot: slot });
      setSuggest(r);
      if (!r.ok && r.reason === 'no_target') toast.info('Előbb állíts be napi célt.');
      if (!r.ok && r.reason === 'no_room') toast.info('Mára már nincs érdemi kalóriakeret.');
    } catch (err) { toast.error(apiError(err, 'Nem sikerült ajánlatot kérni.')); } finally { setBusy(false); }
  };

  const logRecipeOnce = async (r) => {
    setBusy(true);
    try {
      await portalPost('/food-log', {
        date, name: r.name, grams: 0, portions: 1,
        kcal: Math.round(r.kcal), protein: r.protein, carbs: r.carbs, fat: r.fat,
        meal_slot: slot, source: 'recipe',
      });
      toast.success(`${r.name} naplózva (1 adag)`);
      await loadLog();
      if (suggest?.ok) await loadSuggest();
    } catch (err) { toast.error(apiError(err, 'Nem sikerült naplózni.')); } finally { setBusy(false); }
  };

  const toggleDiet = (key) => {
    setDiet(d => (d.includes(key) ? d.filter(x => x !== key) : [...d, key]));
  };

  const saveOwn = async () => {
    setBusy(true);
    try {
      await portalPost('/foods', {
        name: own.name,
        kcal_100: Number(own.kcal_100) || 0,
        protein_100: Number(own.protein_100) || 0,
        carbs_100: Number(own.carbs_100) || 0,
        fat_100: Number(own.fat_100) || 0,
        serving_g: 100,
      });
      toast.success('Saját étel mentve');
      setOwn({ name: '', kcal_100: '', protein_100: '', carbs_100: '', fat_100: '' });
      setShowOwn(false);
      await loadFoods(q);
    } catch (err) { toast.error(apiError(err, 'Nem sikerült menteni.')); } finally { setBusy(false); }
  };

  const useAsTarget = async () => {
    try {
      await portalPut('/food-log/targets', {
        kcal: totals.kcal, protein: totals.protein, carbs: totals.carbs, fat: totals.fat,
        label: 'Saját cél', manual: true,
      });
      toast.success('A mai bevitel lett a napi célod.');
      await loadLog();
    } catch (err) { toast.error(apiError(err, 'Nem sikerült beállítani.')); }
  };

  return (
    <div data-testid="food-diary-tab" className="space-y-6">
      {/* Nap választó + összesítés */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <button data-testid="diary-prev-day" onClick={() => setDate(shiftDate(date, -1))}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"><ChevronLeft size={18} /></button>
          <div className="text-center">
            <div className="text-sm font-bold text-gray-900" data-testid="diary-date">{prettyDate(date)}</div>
            <div className="text-[11px] text-gray-500">{date === todayISO() ? 'ma' : date}</div>
          </div>
          <button data-testid="diary-next-day" onClick={() => setDate(shiftDate(date, 1))}
            disabled={date >= todayISO()}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-30"><ChevronRight size={18} /></button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg bg-gray-900 text-white p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Elfogyasztva</div>
            <div className="text-2xl font-black" data-testid="diary-total-kcal">{hu(totals.kcal)}</div>
            <div className="text-[11px] text-gray-400">kcal</div>
          </div>
          <div className={`rounded-lg p-3 ${targets.kcal > 0 && remaining < 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">
              {targets.kcal <= 0 ? 'Nincs cél' : remaining < 0 ? 'Túllépve' : 'Még beférhet'}
            </div>
            <div className={`text-2xl font-black ${targets.kcal > 0 && remaining < 0 ? 'text-red-600' : 'text-emerald-700'}`}
              data-testid="diary-remaining-kcal">
              {targets.kcal > 0 ? hu(Math.abs(remaining)) : '—'}
            </div>
            <div className="text-[11px] text-gray-500">kcal</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
              Napi cél · {targets.label}
            </div>
            <div className="text-lg font-black text-gray-900" data-testid="diary-target-kcal">
              {targets.kcal > 0 ? `${hu(targets.kcal)} kcal` : 'nincs megadva'}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <MacroBar label="Fehérje" value={totals.protein} target={targets.protein} color="bg-blue-500" />
          <MacroBar label="Szénhidrát" value={totals.carbs} target={targets.carbs} color="bg-amber-500" />
          <MacroBar label="Zsír" value={totals.fat} target={targets.fat} color="bg-rose-500" />
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" variant="outline" data-testid="diary-copy-yesterday" disabled={busy}
            onClick={copyYesterday}>
            <Copy size={14} className="mr-1.5" /> Tegnapi nap másolása
          </Button>
          <Button size="sm" variant="outline" data-testid="diary-use-as-target" disabled={busy || !totals.kcal}
            onClick={useAsTarget}>
            <Target size={14} className="mr-1.5" /> Legyen ez a napi célom
          </Button>
          <Button size="sm" data-testid="diary-suggest-btn" disabled={busy} onClick={loadSuggest}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            <ChefHat size={14} className="mr-1.5" /> Mit ehetek még?
          </Button>
        </div>

        {suggest?.ok && (
          <div data-testid="diary-suggestions" className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="text-xs font-bold text-emerald-900">
                Receptek a maradékhoz · {hu(suggest.kcal_left)} kcal
                {suggest.protein_left > 0 ? ` · még ${hu(suggest.protein_left)} g fehérje` : ''}
              </div>
              <button data-testid="diary-suggestions-close" onClick={() => setSuggest(null)}
                className="text-[11px] font-bold text-emerald-800 hover:underline shrink-0">bezár</button>
            </div>
            <ul className="divide-y divide-emerald-100">
              {suggest.suggestions.map(r => (
                <li key={r.id} data-testid={`diary-suggestion-${r.id}`} className="py-2 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{r.name}</div>
                    <div className="text-[11px] text-gray-600">
                      {hu(r.kcal)} kcal · F {hu(r.protein, 1)} · CH {hu(r.carbs, 1)} · Zs {hu(r.fat, 1)} / adag
                      {r.minutes ? ` · ${r.minutes} perc` : ''}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled={busy}
                    data-testid={`diary-suggestion-add-${r.id}`} onClick={() => logRecipeOnce(r)}
                    className="shrink-0 font-bold">
                    <Plus size={13} className="mr-1" /> 1 adag
                  </Button>
                </li>
              ))}
              {suggest.suggestions.length === 0 && (
                <li className="py-2 text-sm text-gray-600">Nincs a keretbe férő recept.</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Hozzáadás */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Étel vagy recept hozzáadása</h3>

        <div className="inline-flex p-0.5 rounded-full bg-gray-100 mb-3" data-testid="diary-view-switch">
          {[{ v: 'foods', t: 'Ételek' }, { v: 'recipes', t: 'Receptek' }].map(o => (
            <button key={o.v} data-testid={`diary-view-${o.v}`}
              onClick={() => { setView(o.v); setPicked(null); setOpenRecipe(null); }}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${
                view === o.v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
              {o.t}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {SLOTS.map(s => (
            <button key={s.v} data-testid={`diary-slot-${s.v}`} onClick={() => setSlot(s.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                slot === s.v ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s.t}
            </button>
          ))}
        </div>

        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="diary-food-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder={view === 'recipes' ? 'Recept keresése: név vagy hozzávaló…' : 'Keresés: csirkemell, rizs, túró…'}
            className="pl-9" />
        </div>

        {view === 'recipes' && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="diary-diet-filters">
            {[
              { v: 'veget', t: 'Vegetáriánus' },
              { v: 'vegan', t: 'Vegán' },
              { v: 'laktoz', t: 'Laktózmentes' },
              { v: 'feherjedus', t: 'Fehérjedús' },
            ].map(f => (
              <button key={f.v} data-testid={`diary-diet-${f.v}`} onClick={() => toggleDiet(f.v)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                  diet.includes(f.v)
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                {f.t}
              </button>
            ))}
            {diet.length > 0 && (
              <button data-testid="diary-diet-clear" onClick={() => setDiet([])}
                className="px-2 py-1 text-[11px] font-bold text-gray-500 hover:text-gray-900 underline">
                szűrők törlése
              </button>
            )}
          </div>
        )}

        <div className="max-h-56 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
          {listRows.map(f => (
            <div key={f.id} className="hover:bg-gray-50">
              <div className="flex items-center gap-2 px-3 py-2">
                <button onClick={() => toggleFav(f.name)} data-testid={`diary-fav-${f.id}`}
                  className={f.favorite ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}>
                  <Star size={14} fill={f.favorite ? 'currentColor' : 'none'} />
                </button>
                <button data-testid={`diary-pick-${f.id}`}
                  onClick={() => {
                    setPicked(f);
                    if (f.source === 'recipe') setPortions(1);
                    else setGrams(f.serving_g || 100);
                  }}
                  className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
                    {f.source === 'recipe' && (
                      <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide">
                        <ChefHat size={10} /> Recept
                      </span>
                    )}
                    <span className="truncate">{f.name}</span>
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {f.source === 'recipe' ? (
                      <>
                        {hu(f.kcal)} kcal · F {hu(f.protein, 1)} · CH {hu(f.carbs, 1)} · Zs {hu(f.fat, 1)} / adag
                        {f.category ? ` · ${f.category}` : ''}{f.minutes ? ` · ${f.minutes} perc` : ''}
                      </>
                    ) : (
                      <>
                        {hu(f.kcal_100)} kcal · F {hu(f.protein_100, 1)} · CH {hu(f.carbs_100, 1)} · Zs {hu(f.fat_100, 1)} / 100 g
                        {f.source === 'own' && ' · saját'}
                      </>
                    )}
                  </div>
                </button>
                {f.source === 'recipe' && (
                  <button data-testid={`diary-recipe-toggle-${f.id}`}
                    onClick={() => setOpenRecipe(openRecipe === f.id ? null : f.id)}
                    className="text-gray-400 hover:text-gray-800 shrink-0">
                    <ChevronDown size={15} className={openRecipe === f.id ? 'rotate-180 transition-transform' : 'transition-transform'} />
                  </button>
                )}
                {f.source === 'own' && (
                  <button onClick={async () => { await portalDelete(`/foods/${f.id}`); loadFoods(q); }}
                    className="text-gray-300 hover:text-red-500"><Trash2 size={13} /></button>
                )}
              </div>
              {f.source === 'recipe' && openRecipe === f.id && (
                <div data-testid={`diary-recipe-details-${f.id}`} className="px-3 pb-3 -mt-1 text-[12px] text-gray-700 space-y-1.5">
                  <div className="flex flex-wrap gap-1.5">
                    {f.category && <span className="px-1.5 py-0.5 rounded bg-gray-100 font-semibold">{f.category}</span>}
                    {f.minutes ? (
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 font-semibold inline-flex items-center gap-1">
                        <Clock size={10} /> {f.minutes} perc
                      </span>
                    ) : null}
                    {f.vegan && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-semibold">vegán</span>}
                    {!f.vegan && f.vegetarian && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-semibold">vegetáriánus</span>}
                    {f.lactose_free && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">laktózmentes</span>}
                    {f.high_protein && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">fehérjedús</span>}
                  </div>
                  {f.ingredients && <p><span className="font-bold">Hozzávalók:</span> {f.ingredients}</p>}
                  {f.steps && <p><span className="font-bold">Elkészítés:</span> {f.steps}</p>}
                  {f.swaps && <p className="text-gray-500"><span className="font-bold">Csere:</span> {f.swaps}</p>}
                </div>
              )}
            </div>
          ))}
          {listRows.length === 0 && <div className="px-3 py-4 text-sm text-gray-500">Nincs találat.</div>}
        </div>

        {picked && (
          <div data-testid="diary-picked-row" className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
            <div className="text-sm font-bold text-gray-900">{picked.name}</div>
            {isRecipe ? (
              <>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {[0.5, 1, 1.5, 2].map(m => (
                    <button key={m} data-testid={`diary-portion-${m}`} onClick={() => setPortions(m)}
                      className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                        Number(portions) === m ? 'bg-gray-900 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                      {hu(m, 1)} adag
                    </button>
                  ))}
                  <Input data-testid="diary-portions-input" type="number" step="0.1" min="0.1" max="20"
                    value={portions} onChange={e => setPortions(e.target.value)} className="w-20 h-8" />
                  <Button size="sm" data-testid="diary-add-btn" disabled={busy} onClick={addPicked}
                    className="ml-auto bg-gray-900 hover:bg-gray-800 text-white font-bold">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    <span className="ml-1">Naplózom</span>
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 mt-2">
                <Input data-testid="diary-grams-input" type="number" min="1" max="5000" value={grams}
                  onChange={e => setGrams(e.target.value)} className="w-24" />
                <span className="text-sm text-gray-600">gramm</span>
                <Button size="sm" data-testid="diary-add-btn" disabled={busy} onClick={addPicked}
                  className="ml-auto bg-gray-900 hover:bg-gray-800 text-white font-bold">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  <span className="ml-1">Naplózom</span>
                </Button>
              </div>
            )}
            {computed && (
              <div className="text-xs text-gray-700 mt-2">
                {hu(computed.kcal)} kcal · F {hu(computed.protein, 1)} g · CH {hu(computed.carbs, 1)} g · Zs {hu(computed.fat, 1)} g
              </div>
            )}
          </div>
        )}

        {/* AI becslés */}
        <div className="mt-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200">
          <div className="text-xs font-bold text-indigo-900 mb-2 flex items-center gap-1.5">
            <Sparkles size={14} /> Nincs a listában? Írd le, és megbecsüljük
          </div>
          <div className="flex gap-2">
            <Input data-testid="diary-ai-input" maxLength={300} value={aiText} onChange={e => setAiText(e.target.value)}
              placeholder="pl. 2 szelet rozskenyér sajttal és egy alma" />
            <Button size="sm" data-testid="diary-ai-btn" disabled={busy || aiText.trim().length < 3}
              onClick={aiEstimate} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold shrink-0">
              {busy ? <Loader2 size={14} className="animate-spin" /> : 'Becslés'}
            </Button>
          </div>
        </div>

        {/* Saját étel */}
        {view === 'foods' && (showOwn ? (
          <div className="mt-4 p-3 rounded-lg border border-gray-200">
            <div className="text-xs font-bold text-gray-900 mb-2">Saját étel (100 g adatai)</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Input placeholder="Név" data-testid="own-food-name" value={own.name}
                onChange={e => setOwn({ ...own, name: e.target.value })} className="col-span-2 sm:col-span-1" />
              <Input placeholder="kcal" type="number" min="0" max="1000" data-testid="own-food-kcal" value={own.kcal_100}
                onChange={e => setOwn({ ...own, kcal_100: e.target.value })} />
              <Input placeholder="Fehérje" type="number" data-testid="own-food-protein" value={own.protein_100}
                onChange={e => setOwn({ ...own, protein_100: e.target.value })} />
              <Input placeholder="Szénh." type="number" data-testid="own-food-carbs" value={own.carbs_100}
                onChange={e => setOwn({ ...own, carbs_100: e.target.value })} />
              <Input placeholder="Zsír" type="number" data-testid="own-food-fat" value={own.fat_100}
                onChange={e => setOwn({ ...own, fat_100: e.target.value })} />
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" data-testid="own-food-save" disabled={busy || own.name.length < 2} onClick={saveOwn}
                className="bg-gray-900 text-white font-bold">Mentés</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowOwn(false)}>Mégse</Button>
            </div>
          </div>
        ) : (
          <button data-testid="own-food-open" onClick={() => setShowOwn(true)}
            className="mt-3 text-xs font-bold text-gray-600 hover:text-gray-900 underline">
            + Saját étel felvétele
          </button>
        ))}
      </div>

      {/* Mai bejegyzések */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">A nap bejegyzései</h3>
        {!log ? (
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Betöltés…
          </div>
        ) : log.entries.length === 0 ? (
          <p className="text-sm text-gray-500">Még nincs bejegyzés ezen a napon.</p>
        ) : (
          SLOTS.filter(s => log.entries.some(e => e.meal_slot === s.v)).map(s => (
            <div key={s.v} className="mb-4 last:mb-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">{s.t}</div>
              <ul className="divide-y divide-gray-100">
                {log.entries.filter(e => e.meal_slot === s.v).map(e => (
                  <li key={e.id} data-testid={`diary-entry-${e.id}`}
                    className="py-2 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {e.name}
                        {e.portions ? ` · ${hu(e.portions, 1)} adag` : (e.grams ? ` · ${hu(e.grams)} g` : '')}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {hu(e.kcal)} kcal · F {hu(e.protein, 1)} · CH {hu(e.carbs, 1)} · Zs {hu(e.fat, 1)}
                      </div>
                    </div>
                    <button data-testid={`diary-delete-${e.id}`} onClick={() => removeEntry(e.id)}
                      className="text-gray-300 hover:text-red-500"><Trash2 size={15} /></button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FoodDiaryTab;
